import React, { useRef, useEffect } from 'react';

/**
 * Tracks clicks outside of the element tagged with the returned ref and runs
 * the provided callback method when an outside click occurs.
 *
 * Note: You must attach the returned ref to the element of interest.
 *
 * @param {function} callback the function that needs to be run on an outside click
 * @returns {React.MutableRefObject<undefined>}
 *
 * @example
 * const onOutsideClick = () => { alert('Clicked outside!') };
 * const ref = useOutsideClick(onOutsideClick);
 *
 * <div ref={ref}>Test</div>
 */
const useOutsideClick = (callback) => {
  const ref = useRef();

  useEffect(() => {
    const handleClick = (event) => {
      if (ref.current && !ref.current.contains(event.target)) {
        callback();
      }
    };

    document.addEventListener('click', handleClick, true);

    return () => {
      document.removeEventListener('click', handleClick, true);
    };
  }, [ref]);

  return ref;
};

export default useOutsideClick;
